import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { defaultBillingDayOfWeek, getDefaultBillingDayOfYear, repeatableManualBillingDayOfMonth } from '~/constants';
import { Accounting } from '~/types/accounting';
import { BillingTriggerType } from '~/types/api.model';
import { BillingDayOfMonthModel } from '../BillingDayOfMonth';
import { OneTimeInStagesModel } from '../OneTimeInStagesModel';
import * as serviceBillingModel from '../service-billing';
import { toDBRecurrencePeriod } from '../service-billing/translator';
import * as serviceCostModel from '../service-cost';
import * as serviceTemplateBundleModel from '../service-template-bundle';
import { ServiceIntegrationsModel } from '../ServiceIntegrations';
import { makeServiceTemplate, makeBillingOnApproval, makeBillingOnDemand, makeBillingOneTimeManual, makeBillingOngoing, makeBillingRepeatableManual, generateId, makeBillingOneTimeInStages } from './makers';
import { ServiceTemplateType } from './model';
import * as serviceTemplateModel from './selectors';
export function billingFromDTO(billedUpon) {
    switch(billedUpon.type){
        case BillingTriggerType.OnApproval:
            return makeBillingOnApproval();
        case BillingTriggerType.OneTimeManual:
            return makeBillingOneTimeManual();
        case BillingTriggerType.Ongoing:
            return makeBillingOngoing(billedUpon.ongoing.recurrence.period.type);
        case BillingTriggerType.RepeatableManual:
            return makeBillingRepeatableManual(billedUpon.repeatableManual.recurrence.period.type);
        case BillingTriggerType.OneTimeInStages:
            return makeBillingOneTimeInStages(OneTimeInStagesModel.fromDTO(billedUpon.oneTimeInStages));
        case BillingTriggerType.OnDemand:
            return makeBillingOnDemand(billedUpon.onDemand.preapprovalPeriod);
    }
}
export function billingToDTO(billing) {
    return serviceTemplateModel.matchBilling(billing, {
        onApproval: ()=>({
                type: BillingTriggerType.OnApproval
            }),
        oneTimeManual: ()=>({
                type: BillingTriggerType.OneTimeManual
            }),
        ongoing: (ongoing)=>({
                type: BillingTriggerType.Ongoing,
                ongoing: {
                    isPaused: false,
                    prorateFirstInvoice: false,
                    billUpfront: false,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    },
                    recurrence: {
                        period: toDBRecurrencePeriod(ongoing.recurrencePeriod, {
                            dayOfMonth: BillingDayOfMonthModel.make(),
                            dayOfWeek: defaultBillingDayOfWeek,
                            dayOfYear: getDefaultBillingDayOfYear()
                        })
                    }
                }
            }),
        repeatableManual: (repeatableManual)=>({
                type: BillingTriggerType.RepeatableManual,
                repeatableManual: {
                    billUpfront: true,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    },
                    recurrence: {
                        period: toDBRecurrencePeriod(repeatableManual.recurrencePeriod, {
                            dayOfMonth: BillingDayOfMonthModel.make(repeatableManualBillingDayOfMonth),
                            dayOfWeek: defaultBillingDayOfWeek,
                            dayOfYear: getDefaultBillingDayOfYear()
                        })
                    }
                }
            }),
        oneTimeInStages: (oneTimeInStages)=>({
                type: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: oneTimeInStages.oneTimeInStages.toDTO()
            }),
        onDemand: (onDemand)=>({
                type: BillingTriggerType.OnDemand,
                onDemand: {
                    preapprovalPeriod: onDemand.preapprovalPeriod,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    }
                }
            })
    });
}
export function fromService(service, oldServiceTemplate) {
    const { billing, terms } = service;
    function makeBilling() {
        return serviceBillingModel.match(billing, {
            onApproval: makeBillingOnApproval,
            oneTimeManual: makeBillingOneTimeManual,
            ongoing: (ongoing)=>makeBillingOngoing(ongoing.recurrencePeriod),
            repeatableManual: (repeatableManual)=>makeBillingRepeatableManual(repeatableManual.recurrencePeriod),
            oneTimeInStages: (oneTimeInStages)=>makeBillingOneTimeInStages(oneTimeInStages.oneTimeInStages),
            onDemand: (onDemand)=>makeBillingOnDemand(onDemand.preapprovalPeriod)
        });
    }
    return makeServiceTemplate(_object_spread_props(_object_spread({}, oldServiceTemplate), {
        id: service.serviceTemplateId,
        name: service.name,
        description: service.description,
        terms,
        cost: service.cost,
        integrations: service.integrations,
        billing: makeBilling(),
        taxable: service.taxable,
        bundle: service.bundle ? serviceTemplateBundleModel.fromService(service) : undefined
    }));
}
export function fromDTO(dto) {
    var _dto_terms, _dto_taxable;
    const common = {
        id: dto.id,
        name: dto.name,
        description: dto.description,
        terms: (_dto_terms = dto.terms) !== null && _dto_terms !== void 0 ? _dto_terms : '',
        status: dto.status,
        cost: serviceCostModel.fromDBDTO(dto.cost),
        billing: billingFromDTO(dto.billedUpon),
        origin: dto.origin || null,
        integrations: ServiceIntegrationsModel.fromDTO(dto.integrations),
        usedIn: dto.usedIn || [],
        referenceFields: dto.referenceFields
    };
    return dto.bundle ? _object_spread_props(_object_spread({}, common), {
        bundle: serviceTemplateBundleModel.fromDTO(dto.bundle)
    }) : _object_spread_props(_object_spread({}, common), {
        taxable: (_dto_taxable = dto.taxable) !== null && _dto_taxable !== void 0 ? _dto_taxable : undefined
    });
}
export function toDTO(service) {
    var _service_taxable;
    const dto = _object_spread({
        id: service.id || generateId(),
        name: service.name,
        description: service.description,
        terms: service.terms,
        status: service.status,
        cost: serviceCostModel.toDBDTO(service.cost),
        billedUpon: billingToDTO(service.billing),
        usedIn: service.usedIn,
        taxable: (_service_taxable = service.taxable) !== null && _service_taxable !== void 0 ? _service_taxable : null,
        referenceFields: service.referenceFields
    }, service.serviceContentObj && {
        serviceContentObj: service.serviceContentObj
    }, service.bundle ? {
        type: ServiceTemplateType.Bundle,
        bundle: serviceTemplateBundleModel.toDTO(service.bundle)
    } : {
        type: ServiceTemplateType.Service
    });
    const integratedId = serviceTemplateModel.integratedId({
        serviceTemplate: service,
        accounting: Accounting.QBO
    });
    if (integratedId) {
        dto.integratedId = integratedId;
    }
    if (service.origin) {
        dto.origin = service.origin;
    }
    if (service.integrations.hasIntegrations()) {
        dto.integrations = service.integrations.toJSON();
    }
    return dto;
}
